import React, { useState, useEffect, useRef } from 'react';
// Safe fallback if @livekit/track-processors doesn't export this
const isBackgroundProcessorSupported = () => {
  try {
    return typeof window !== 'undefined' && 'MediaStreamTrackProcessor' in window;
  } catch { return false; }
};
import { Button } from '@/components/ui/button';
import { Upload, X, Ban, CloudFog } from 'lucide-react';
import { cn } from '@/lib/utils';

const PRESET_BACKGROUNDS = [
  { id: 'none', label: 'None', type: 'none', thumbnail: null },
  { id: 'blur-light', label: 'Light Blur', type: 'blur', blurRadius: 6 },
  { id: 'blur-heavy', label: 'Heavy Blur', type: 'blur', blurRadius: 15 },
  { id: 'office', label: 'Office', type: 'image', src: '/backgrounds/office.jpg', thumbnail: '/backgrounds/thumbs/office.jpg' },
  { id: 'nature', label: 'Nature', type: 'image', src: '/backgrounds/nature.jpg', thumbnail: '/backgrounds/thumbs/nature.jpg' },
  { id: 'abstract-blue', label: 'Abstract Blue', type: 'image', src: '/backgrounds/abstract-blue.jpg', thumbnail: '/backgrounds/thumbs/abstract-blue.jpg' },
  { id: 'bookshelf', label: 'Bookshelf', type: 'image', src: '/backgrounds/bookshelf.jpg', thumbnail: '/backgrounds/thumbs/bookshelf.jpg' },
  { id: 'cityscape', label: 'Cityscape', type: 'image', src: '/backgrounds/cityscape.jpg', thumbnail: '/backgrounds/thumbs/cityscape.jpg' },
  { id: 'gradient', label: 'Gradient', type: 'image', src: '/backgrounds/gradient.jpg', thumbnail: '/backgrounds/thumbs/gradient.jpg' },
];

const CUSTOM_STORAGE_KEY = 'pf-custom-backgrounds';
const MAX_CUSTOM = 5;

function loadCustomBackgrounds() {
  try {
    return JSON.parse(localStorage.getItem(CUSTOM_STORAGE_KEY) || '[]');
  } catch {
    return [];
  }
}

function saveCustomBackgrounds(items) {
  try {
    localStorage.setItem(CUSTOM_STORAGE_KEY, JSON.stringify(items));
  } catch {
    // localStorage full
  }
}

/**
 * Grid selector for virtual backgrounds (presets + custom uploads).
 * Hides entirely if the browser doesn't support background processing.
 *
 * @param {{ selectedId: string|null, onSelect: (bg: object) => void, disabled?: boolean }} props
 */
export default function BackgroundSelector({ selectedId, onSelect, disabled = false }) {
  const [supported, setSupported] = useState(null); // null = checking
  const [customImages, setCustomImages] = useState(loadCustomBackgrounds);
  const fileInputRef = useRef(null);

  useEffect(() => {
    setSupported(isBackgroundProcessorSupported());
  }, []);

  // Still checking support
  if (supported === null) return null;

  // Unsupported browser
  if (!supported) {
    return (
      <p className="text-xs text-muted-foreground py-2">
        Virtual backgrounds are not supported in this browser.
      </p>
    );
  }

  const handleUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (customImages.length >= MAX_CUSTOM) {
      alert(`You can upload up to ${MAX_CUSTOM} custom backgrounds. Delete one first.`);
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      const id = `custom-${Date.now()}`;
      const newImg = {
        id,
        label: file.name.replace(/\.[^.]+$/, ''),
        type: 'image',
        src: dataUrl,
        thumbnail: dataUrl,
        custom: true,
      };
      const updated = [...customImages, newImg];
      setCustomImages(updated);
      saveCustomBackgrounds(updated);
      onSelect(newImg);
    };
    reader.readAsDataURL(file);

    // Reset input so same file can be re-selected
    e.target.value = '';
  };

  const handleDeleteCustom = (e, id) => {
    e.stopPropagation();
    const updated = customImages.filter((img) => img.id !== id);
    setCustomImages(updated);
    saveCustomBackgrounds(updated);

    // If the deleted image was selected, revert to none
    if (selectedId === id) {
      onSelect(PRESET_BACKGROUNDS[0]);
    }
  };

  const allOptions = [...PRESET_BACKGROUNDS, ...customImages];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium">Background</label>
        {customImages.length < MAX_CUSTOM && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs gap-1"
            disabled={disabled}
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="h-3 w-3" />
            Upload
          </Button>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={handleUpload}
        />
      </div>

      <div className="grid grid-cols-5 gap-2">
        {allOptions.map((bg) => (
          <button
            key={bg.id}
            onClick={() => onSelect(bg)}
            disabled={disabled}
            title={bg.label}
            className={cn(
              'relative rounded-lg border-2 overflow-hidden aspect-video',
              'hover:border-primary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              'transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
              selectedId === bg.id
                ? 'border-primary ring-2 ring-primary/20'
                : 'border-muted',
            )}
          >
            {/* None */}
            {bg.type === 'none' && (
              <div className="w-full h-full flex flex-col items-center justify-center bg-muted gap-0.5">
                <Ban className="h-4 w-4 text-muted-foreground" />
                <span className="text-[10px] text-muted-foreground">Off</span>
              </div>
            )}

            {/* Blur */}
            {bg.type === 'blur' && (
              <div className="w-full h-full flex flex-col items-center justify-center bg-muted/60 gap-0.5">
                <CloudFog className="h-4 w-4 text-muted-foreground" />
                <span className="text-[10px] text-muted-foreground leading-tight text-center px-0.5">
                  {bg.label}
                </span>
              </div>
            )}

            {/* Image */}
            {bg.type === 'image' && bg.thumbnail && (
              <img
                src={bg.thumbnail}
                alt={bg.label}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            )}

            {/* Delete button for custom images */}
            {bg.custom && (
              <button
                onClick={(e) => handleDeleteCustom(e, bg.id)}
                className={cn(
                  'absolute top-0.5 right-0.5 rounded-full bg-black/60 text-white',
                  'p-0.5 hover:bg-destructive transition-colors',
                )}
                title="Remove custom background"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}
